import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodyLg } from "@/domains/design/components/body-lg"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXl } from "@/domains/design/components/body-xl"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn } from "@/domains/library"

import type { PriceStrategy, PriceStrategyConfig } from "../../stores/price-strategies"

export interface PriceStrategySelectorViewProps {
  strategies: Array<PriceStrategyConfig>
  selectedStrategy: PriceStrategy | undefined
  isDisabled: boolean
  onSelect: (strategy: PriceStrategy) => void
}

/**
 * Stateless View: Price strategies selector
 * Styled to match the tick-spacing selector card-grid pattern.
 */
export function PriceStrategySelectorView({
  strategies,
  selectedStrategy,
  isDisabled,
  onSelect,
}: PriceStrategySelectorViewProps) {
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="text-sm font-medium text-t100">Price strategies</div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {strategies.map((strategy) => {
          const isSelected = strategy.key === selectedStrategy

          return (
            <button
              key={strategy.key}
              type="button"
              onClick={() => onSelect(strategy.key)}
              disabled={isDisabled}
              className={cn(
                "group relative flex w-full flex-col items-start justify-between gap-2 rounded-lg border px-4 py-3 text-left transition-all",
                isDisabled
                  ? "cursor-not-allowed border-p400-light bg-p400/50 opacity-60"
                  : isSelected
                    ? "border-p100 bg-p300-light shadow-sm hover:border-p100 hover:bg-p300-light"
                    : "border-p400-light bg-p400 hover:border-p100 hover:bg-p300-light",
              )}
            >
              <div className="flex w-full flex-col gap-4">
                <BodyXs As="span" className={cn(isSelected ? "text-t100" : "text-t200")}>
                  {strategy.title}
                </BodyXs>

                <BodyLg As="span" className={cn("tracking-tight", isSelected ? "text-t100" : "text-t100")}>
                  {strategy.display}
                </BodyLg>
              </div>

              <BodyCaption As="span" className={cn(isSelected ? "text-t100" : "text-t200")}>
                {strategy.description}
              </BodyCaption>
            </button>
          )
        })}
      </div>
    </div>
  )
}
